import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { vehiclesAPI, subscriptionAPI } from "@/lib/api";
import { OFFROAD_ICON_URL } from "@/lib/assets";
import { toast } from "sonner";
import {
  Plus,
  Truck,
  LogOut,
  Gauge,
  Calendar,
  ChevronRight,
  Loader2,
  Shield,
  BarChart3,
  ClipboardList,
  Bell,
  Package,
  Users,
  Wrench,
  Crown,
  Menu,
  X,
  Lock,
  Heart,
  Tags,
  ArrowLeftRight,
  TrendingUp,
  Route,
  Mountain,
  Settings,
  Bot,
  BookOpen,
} from "lucide-react";

export default function Garage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [vehiclesRes, subRes, usageRes] = await Promise.all([
        vehiclesAPI.list(),
        subscriptionAPI.status(),
        subscriptionAPI.usage()
      ]);
      setVehicles(vehiclesRes.data);
      setSubscription(subRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const isPro = subscription?.is_pro;
  const isAdmin = subscription?.is_admin;
  const vehicleLimit = subscription?.vehicle_limit;
  const canAddVehicle = isPro || (vehicles.length < (vehicleLimit || 2));

  const handleAddVehicle = () => {
    if (!canAddVehicle) {
      toast.error(`Free tier is limited to ${vehicleLimit} vehicles. Upgrade to Pro for unlimited!`);
      navigate("/subscription");
      return;
    }
    navigate("/vehicles/new");
  };

  // Pro-only navigation items
  const proFeatures = [
    { to: "/shops", icon: Wrench, label: "Shops", testId: "shops-btn" },
    { to: "/teams", icon: Users, label: "Teams", testId: "teams-btn" },
    { to: "/parts", icon: Package, label: "Parts", testId: "parts-btn" },
    { to: "/reminders", icon: Bell, label: "Reminders", testId: "reminders-btn" },
    { to: "/analytics", icon: BarChart3, label: "Analytics", testId: "analytics-btn" },
    { to: "/templates", icon: ClipboardList, label: "Templates", testId: "templates-btn" },
    { to: "/categories", icon: Tags, label: "Categories", testId: "categories-btn" },
    { to: "/compare", icon: ArrowLeftRight, label: "Compare", testId: "compare-btn" },
    { to: "/forecast", icon: TrendingUp, label: "Forecast", testId: "forecast-btn" },
    { to: "/trips", icon: Route, label: "Trips", testId: "trips-btn" },
    { to: "/ai", icon: Bot, label: "AI", testId: "ai-btn" },
  ];

  // Free features (available to everyone)
  const freeFeatures = [
    { to: "/wishlist", icon: Heart, label: "Wishlist", testId: "wishlist-btn" },
    { to: "/community-trails", icon: Mountain, label: "Trails", testId: "community-trails-btn" },
    { to: "/knowledge-base", icon: BookOpen, label: "Tips", testId: "knowledge-base-btn" },
  ];

  const NavLink = ({ to, icon: Icon, label, testId, proOnly = false }) => {
    const isLocked = proOnly && !isPro;
    
    if (isLocked) {
      return (
        <button
          onClick={() => {
            toast.info(`${label} is a Pro feature. Upgrade to unlock!`);
            navigate("/subscription");
          }}
          className="text-zinc-600 hover:text-zinc-400 transition-colors relative"
          title={`${label} (Pro)`}
        >
          <Icon className="w-5 h-5" />
          <Lock className="w-2.5 h-2.5 absolute -top-1 -right-1 text-amber-500" />
        </button>
      );
    }
    
    return (
      <Link
        to={to}
        data-testid={testId}
        className="text-muted-foreground hover:text-foreground transition-colors"
        title={label}
      >
        <Icon className="w-5 h-5" />
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-emerald-50 pb-20 md:pb-0">
      {/* Header */}
      <header className="border-b border-emerald-200 bg-emerald-100/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center border border-amber-500/30 p-1.5">
                <img src={OFFROAD_ICON_URL} alt="Off-Road Truck" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800 tracking-tight">
                  Dynamite Off-Road
                </h1>
                <p className="text-xs text-gray-600">Buildsheet Pro</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {/* Subscription Badge */}
              <Link to="/subscription" className="mr-2">
                {isPro ? (
                  <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30 hover:bg-amber-500/30">
                    <Crown className="w-3 h-3 mr-1" />
                    PRO
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-zinc-600 text-zinc-400 hover:border-amber-500 hover:text-amber-500">
                    <Crown className="w-3 h-3 mr-1" />
                    Upgrade
                  </Badge>
                )}
              </Link>
              
              <span className="text-sm text-muted-foreground hidden sm:block">
                {user.name}
              </span>
              
              {proFeatures.map((item) => (
                <NavLink key={item.to} {...item} proOnly={true} />
              ))}
              
              {freeFeatures.map((item) => (
                <NavLink key={item.to} {...item} proOnly={false} />
              ))}
              
              <NavLink to="/backup" icon={Shield} label="Backup" testId="backup-btn" />
              
              {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="admin-btn"
                  className="text-amber-600 hover:text-amber-800 transition-colors"
                  title="Admin Dashboard"
                >
                  <Settings className="w-5 h-5" />
                </Link>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-muted-foreground"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card px-4 py-4 space-y-3">
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <span className="text-sm text-muted-foreground">{user.name}</span>
              <Link to="/subscription">
                {isPro ? (
                  <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">
                    <Crown className="w-3 h-3 mr-1" />
                    PRO
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-zinc-600 text-zinc-400">
                    <Crown className="w-3 h-3 mr-1" />
                    Upgrade
                  </Badge>
                )}
              </Link>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              {proFeatures.map((item) => (
                <button
                  key={item.to}
                  onClick={() => {
                    if (!isPro) {
                      toast.info(`${item.label} is a Pro feature`);
                      navigate("/subscription");
                    } else {
                      navigate(item.to);
                    }
                    setMobileMenuOpen(false);
                  }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg ${
                    isPro ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-900 text-zinc-600'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-xs">{item.label}</span>
                  {!isPro && <Lock className="w-3 h-3 text-amber-500" />}
                </button>
              ))}
              
              {freeFeatures.map((item) => (
                <button
                  key={item.to}
                  onClick={() => { navigate(item.to); setMobileMenuOpen(false); }}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-zinc-800 text-zinc-300"
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
              
              <button
                onClick={() => { navigate("/backup"); setMobileMenuOpen(false); }}
                className="flex flex-col items-center gap-1 p-3 rounded-lg bg-zinc-800 text-zinc-300"
              >
                <Shield className="w-5 h-5" />
                <span className="text-xs">Backup</span>
              </button>

              {isAdmin && (
                <button
                  onClick={() => { navigate("/admin"); setMobileMenuOpen(false); }}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-amber-500/20 text-amber-700 border border-amber-500/30"
                >
                  <Settings className="w-5 h-5" />
                  <span className="text-xs font-medium">Admin</span>
                </button>
              )}
            </div>
            
            <Button
              variant="outline"
              className="w-full border-zinc-700"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Free Tier Usage Banner */}
        {usage && !usage.is_pro && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-sm">
                  <span className="text-amber-800 font-medium">Free Tier Usage:</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className={`${usage.vehicles.current >= usage.vehicles.limit ? 'text-red-600 font-medium' : 'text-amber-700'}`}>
                    🚗 {usage.vehicles.current}/{usage.vehicles.limit} vehicles
                  </span>
                  <span className={`${usage.photos.current >= usage.photos.limit ? 'text-red-600 font-medium' : 'text-amber-700'}`}>
                    📷 {usage.photos.current}/{usage.photos.limit} photos
                  </span>
                  {usage.vehicle_usage && usage.vehicle_usage[0] && (
                    <>
                      <span className={`${usage.vehicle_usage[0].mods >= usage.vehicle_usage[0].mod_limit ? 'text-red-600 font-medium' : 'text-amber-700'}`}>
                        🔧 {usage.vehicle_usage[0].mods}/{usage.vehicle_usage[0].mod_limit} mods
                      </span>
                      <span className={`${usage.vehicle_usage[0].services >= usage.vehicle_usage[0].service_limit ? 'text-red-600 font-medium' : 'text-amber-700'}`}>
                        🛠️ {usage.vehicle_usage[0].services}/{usage.vehicle_usage[0].service_limit} services
                      </span>
                    </>
                  )}
                </div>
              </div>
              <Link to="/subscription">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Crown className="w-3 h-3 mr-1" />
                  Upgrade to Pro
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
              My Garage
            </h2>
            <p className="text-gray-600 mt-1">
              {vehicles.length}{!isPro && vehicleLimit ? `/${vehicleLimit}` : ""} {vehicles.length === 1 ? "vehicle" : "vehicles"}
              {!isPro && <span className="text-gray-500 ml-2">(Free tier)</span>}
            </p>
          </div>
          <Button
            onClick={handleAddVehicle}
            data-testid="add-vehicle-btn"
            className={`btn-tactical ${canAddVehicle ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-zinc-700 text-zinc-400'}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div
                className="w-full max-w-md h-48 mb-8 bg-cover bg-center rounded-sm opacity-60"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1702146713858-8e7d1cc29fe8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtZWNoYW5pYyUyMHdvcmtpbmclMjBvbiUyMHN1c3BlbnNpb258ZW58MHx8fHwxNzY4ODI4MDkyfDA&ixlib=rb-4.1.0&q=85')`,
                }}
              />
              <h3 className="text-xl font-bold text-foreground mb-2">
                No Vehicles Yet
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm">
                Add your first rig to start tracking builds and maintenance
              </p>
              <Button
                onClick={() => navigate("/vehicles/new")}
                data-testid="add-first-vehicle-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Rig
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((vehicle) => (
              <Link
                key={vehicle.id}
                to={`/vehicles/${vehicle.id}`}
                data-testid={`vehicle-card-${vehicle.id}`}
                className="block group"
              >
                <Card className="bg-emerald-50 border border-emerald-200 rounded-sm hover:bg-sky-100 hover:border-sky-300 transition-all duration-200 h-full">
                  <CardContent className="p-0">
                    {/* Vehicle Image */}
                    <div className="h-40 bg-zinc-900 relative overflow-hidden">
                      {vehicle.primary_photo ? (
                        <img
                          src={vehicle.primary_photo}
                          alt={vehicle.nickname}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Truck className="w-16 h-16 text-emerald-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-emerald-100 to-transparent" />
                    </div>

                    {/* Vehicle Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-lg font-bold text-gray-800 truncate">
                            {vehicle.nickname}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {[vehicle.year, vehicle.make, vehicle.model]
                              .filter(Boolean)
                              .join(" ") || "Vehicle details not set"}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-sky-600 transition-colors flex-shrink-0" />
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-emerald-200">
                        <div className="flex items-center gap-2 text-sm">
                          <Gauge className="w-4 h-4 text-gray-500" />
                          <span className="font-mono text-gray-800">
                            {vehicle.mileage_current?.toLocaleString() || "---"}
                          </span>
                          <span className="text-gray-500">mi</span>
                        </div>
                        {vehicle.created_at && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(vehicle.created_at).toLocaleDateString(
                                "en-US",
                                { month: "short", year: "numeric" }
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-zinc-800 px-2 py-2 z-50">
        <div className="flex items-center justify-around">
          <button
            onClick={() => navigate("/")}
            className="flex flex-col items-center gap-1 p-2 text-amber-500"
          >
            <Truck className="w-5 h-5" />
            <span className="text-xs">Garage</span>
          </button>
          <button
            onClick={handleAddVehicle}
            className="flex flex-col items-center gap-1 p-2 text-zinc-400"
          >
            <Plus className="w-5 h-5" />
            <span className="text-xs">Add</span>
          </button>
          <button
            onClick={() => navigate("/subscription")}
            className="flex flex-col items-center gap-1 p-2 text-zinc-400"
          >
            <Crown className={`w-5 h-5 ${isPro ? 'text-amber-500' : ''}`} />
            <span className="text-xs">{isPro ? 'Pro' : 'Upgrade'}</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex flex-col items-center gap-1 p-2 text-zinc-400"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-xs">Logout</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
